import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { commentApi, type CreateCommentPayload } from "lib/api";
import { Comment } from "util/constants";

interface CommentsByCommentableQueryParams {
  /** Legacy single-kind filter. Pass `kinds` for multi-kind allowlists. */
  kind?: string;
  /**
   * Explicit kind allowlist forwarded to the backend as
   * `?kinds=kind1,kind2`. Bypasses the default Discussion exclusion
   * (`task`/`principle`/`edge_note`) and the single-`kind` filter.
   * When the surface is an issue Discussion AND this list contains
   * `'argument'`, the backend inlines argument rows attached to
   * this issue's child teabates.
   */
  kinds?: ReadonlyArray<string>;
}

const normalizeKindsForKey = (
  kinds: ReadonlyArray<string> | undefined
): string => {
  if (!Array.isArray(kinds) || kinds.length === 0) return "default";
  const cleaned = Array.from(
    new Set(
      kinds
        .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
        .filter((value) => value.length > 0)
    )
  ).sort();
  return cleaned.length > 0 ? cleaned.join(",") : "default";
};

export const commentQueryKeys = {
  byCommentable: (
    commentableType: Comment["commentable_type"],
    commentableId: number,
    kind?: string | null,
    kinds?: ReadonlyArray<string>
  ) =>
    [
      "comments",
      "byCommentable",
      commentableType,
      commentableId,
      kind ?? "all",
      normalizeKindsForKey(kinds),
    ] as const,
};

export const useCommentsByCommentableQuery = (
  commentableType: Comment["commentable_type"],
  commentableId: number | null | undefined,
  params?: CommentsByCommentableQueryParams
) =>
  useQuery({
    queryKey:
      typeof commentableId === "number"
        ? commentQueryKeys.byCommentable(
            commentableType,
            commentableId,
            params?.kind,
            params?.kinds
          )
        : [
            "comments",
            "byCommentable",
            commentableType,
            "empty",
            params?.kind ?? "all",
            normalizeKindsForKey(params?.kinds),
          ],
    queryFn: () => commentApi.getByCommentable(commentableType, commentableId as number, params),
    enabled: typeof commentableId === "number" && commentableId > 0,
  });

export const useCreateCommentMutation = (commentableType: Comment["commentable_type"], commentableId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCommentPayload) => commentApi.createForCommentable(commentableType, commentableId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["comments", "byCommentable", commentableType, commentableId],
      });
    },
  });
};

